import React, { useMemo } from "react";
import { usePlantSelection } from "../../features/plants/PlantSelectionContext";
import useOperationMapping from "../masters/useOperationMapping";
import OperationMappingFields from "../masters/OperationMappingFields";
import {
  useAddItemMutation,
  useDeleteItemMutation,
  useGetItemsQuery,
  useUpdateItemMutation,
} from "../../features/items/itemApi";
import { useLazyPlantOptions } from "../../features/plants/usePlantOptions";
import MasterCrudPage from "../masters/MasterCrudPage";
import { withAuditFilters } from "../masters/filterMetadata";
import { getPlantLabel, renderPlantCell } from "../masters/masterMetadata";

const itemFilterFields = withAuditFilters([
  { name: "item_id", label: "Item ID", optionSource: "items" },
  { name: "item_name", label: "Item Name" },
  { name: "item_code", label: "Item Code" },
]);

const Item = () => {
  const { selectedPlantId } = usePlantSelection();
  const mapping = useOperationMapping("item", selectedPlantId);
  const {
    options: plantOptions,
    isFetching: isLoadingPlants,
    loadOptions: loadPlantOptions,
  } = useLazyPlantOptions();

  const fields = useMemo(
    () => [
      {
        name: "plant_id",
        label: "Plant",
        type: "select",
        options: plantOptions,
        placeholder: isLoadingPlants ? "Loading plants..." : "Select plant",
        disabled: isLoadingPlants && plantOptions.length === 0,
        disabledOnEdit: true,
        required: true,
        fullWidth: true,
      },
      {
        name: "item_name",
        label: "Item Name",
        placeholder: "Enter item name",
        required: true,
      },
      {
        name: "item_code",
        label: "Item Code",
        placeholder: "Enter item code",
        required: true,
      },
      {
        name: "production_cycle_time",
        label: "Production Cycle Time (seconds)",
        type: "number",
        placeholder: "Enter production cycle time",
        min: 0,
        step: "any",
      },
    ],
    [isLoadingPlants, plantOptions]
  );

  const columns = useMemo(
    () => (metaData) => [
      {
        name: "Item ID",
        selector: (row) => row?.item_id || "-",
        sortable: true,
      },
      {
        name: "Item Name",
        selector: (row) => row?.item_name || "-",
        sortable: true,
        grow: 2,
      },
      {
        name: "Item Code",
        selector: (row) => row?.item_code || "-",
        sortable: true,
      },
      {
        name: "Plant",
        selector: (row) => getPlantLabel(row, metaData),
        cell: (row) => renderPlantCell(row, metaData),
        sortable: true,
        grow: 2,
      },
    ],
    []
  );

  return (
    <MasterCrudPage
      entityName="Item"
      pageTitle="Items"
      idField="item_id"
      displayField="item_name"
      fields={fields}
      columns={columns}
      searchFields={[
        "item_id",
        "item_name",
        "item_code",
        "plant_id",
        (row, metaData) => getPlantLabel(row, metaData),
      ]}
      searchPlaceholder="Search items"
      useListQuery={useGetItemsQuery}
      useCreateMutation={useAddItemMutation}
      useUpdateMutation={useUpdateItemMutation}
      useDeleteMutation={useDeleteItemMutation}
      filterFields={itemFilterFields}
      transformPayload={(payload, editData) => {
        if (!editData) return payload;
        const updatePayload = { ...payload };
        delete updatePayload.plant_id;
        return updatePayload;
      }}
      onOpenForm={(row) => {
        loadPlantOptions();
        mapping.load(row);
      }}
      onAfterSave={mapping.save}
      formDisabled={mapping.isLoading || Boolean(mapping.error) || mapping.value.some((id) => !id)}
      extraFormContent={(isSaving) => <OperationMappingFields mapping={mapping} ordered disabled={isSaving} />}
      noDataLabel="No items found"
    />
  );
};

export default Item;
